// components/receptionist/VitalsModal.jsx
import React, { useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Activity } from "lucide-react";

const VitalsModal = ({ show, onHide, vitalsData, setVitalsData, onSubmit, loading, patientInfo }) => {
  // Auto-calculate BMI when weight and height change
  useEffect(() => {
    if (vitalsData.weight && vitalsData.height) {
      const heightInMeters = vitalsData.height / 100;
      const bmi = (vitalsData.weight / (heightInMeters * heightInMeters)).toFixed(1);
      setVitalsData((prev) => ({ ...prev, bmi }));
    }
  }, [vitalsData.weight, vitalsData.height, setVitalsData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setVitalsData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: "2px solid #f0f0f0" }}>
        <Modal.Title className="d-flex align-items-center">
          <Activity size={24} className="me-2" style={{ color: "#667eea" }} />
          Record Vital Signs
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {patientInfo && (
          <div
            className="alert alert-info mb-4"
            style={{ borderRadius: "10px", background: "#e3f2fd", border: "none" }}
          >
            <strong>Patient:</strong> {patientInfo.pName} | <strong>MR-Code:</strong> {patientInfo.mrCode}
          </div>
        )}

        <p className="text-muted mb-4">
          Please record the patient's vital signs before confirming the appointment.
        </p>

        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label fw-semibold">
              Blood Pressure
              <span className="text-muted ms-1" style={{ fontSize: "0.85rem" }}>
                (mmHg)
              </span>
            </label>
            <input
              type="text"
              name="bp"
              className="form-control"
              value={vitalsData.bp}
              onChange={handleChange}
              placeholder="120/80"
              style={{ borderRadius: "8px" }}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label fw-semibold">
              Weight
              <span className="text-muted ms-1" style={{ fontSize: "0.85rem" }}>
                (kg)
              </span>
            </label>
            <input
              type="number"
              name="weight"
              className="form-control"
              value={vitalsData.weight}
              onChange={handleChange}
              placeholder="70"
              step="0.1"
              style={{ borderRadius: "8px" }}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label fw-semibold">
              SPO2
              <span className="text-muted ms-1" style={{ fontSize: "0.85rem" }}>
                (%)
              </span>
            </label>
            <input
              type="number"
              name="spo2"
              className="form-control"
              value={vitalsData.spo2}
              onChange={handleChange}
              placeholder="98"
              min="0"
              max="100"
              style={{ borderRadius: "8px" }}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label fw-semibold">
              Temperature
              <span className="text-muted ms-1" style={{ fontSize: "0.85rem" }}>
                (°F)
              </span>
            </label>
            <input
              type="number"
              name="temperature"
              className="form-control"
              value={vitalsData.temperature}
              onChange={handleChange}
              placeholder="98.6"
              step="0.1"
              style={{ borderRadius: "8px" }}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label fw-semibold">
              Height
              <span className="text-muted ms-1" style={{ fontSize: "0.85rem" }}>
                (cm)
              </span>
            </label>
            <input
              type="number"
              name="height"
              className="form-control"
              value={vitalsData.height}
              onChange={handleChange}
              placeholder="170"
              step="0.1"
              style={{ borderRadius: "8px" }}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label fw-semibold">
              BMI
              <span className="text-muted ms-1" style={{ fontSize: "0.85rem" }}>
                (auto)
              </span>
            </label>
            <input
              type="text"
              name="bmi"
              className="form-control"
              value={vitalsData.bmi}
              disabled
              placeholder="Auto-calculated"
              style={{ borderRadius: "8px", background: "#f5f5f5" }}
            />
          </div>

          <div className="col-12">
            <label className="form-label fw-semibold">Additional Notes</label>
            <textarea
              name="addNotes"
              className="form-control"
              value={vitalsData.addNotes}
              onChange={handleChange}
              rows="3"
              placeholder="Any additional observations or notes..."
              style={{ borderRadius: "8px" }}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "2px solid #f0f0f0" }}>
        <button
          className="btn btn-outline-secondary"
          onClick={onHide}
          disabled={loading}
          style={{ borderRadius: "8px", minWidth: "100px" }}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={onSubmit}
          disabled={loading}
          style={{ borderRadius: "8px", minWidth: "150px" }}
        >
          {loading ? "Confirming..." : "Confirm Appointment"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default VitalsModal;